/**
 * محاسبات رشد ماه‌به‌ماه مشترک بین «جدول عملکرد ماهانه» و «نبض بازار».
 * منبع: مقادیر invoiceCount، totalAmount، avgItemPrice، avgItemCount
 */

export interface ClubGrowthRow {
  month: string;
  monthNum: string | number;
  year?: number;
  invoiceCount: number;
  totalAmount: number;
  avgItemPrice: number;
  avgItemCount: number;
  /** تعداد روزهای دارای داده در ماه (از BI_BranchDetails.OrderDays) */
  orderDays?: number | null;
  /**
   * فقط برای ردیف‌های «بازار» نبض بازار: مجموع (نه میانگین) فاکتورهای همه شعب واجدشرایط در این ماه.
   * برای محاسبه «کل فاکتورهای بررسی‌شده» در badge اعتماد استفاده می‌شود.
   */
  sumInvoiceCount?: number;
}

export interface IncompleteMonthInfo {
  incomplete: boolean;
  orderDays: number;
  fullDays: number;
}

export type RowGrowthType =
  | 'invoiceCountGrowth'
  | 'totalAmountGrowth'
  | 'priceGrowth'
  | 'basketGrowth';

export type MarketPulseGrowthKey =
  | 'salesGrowth'
  | 'invoiceGrowth'
  | 'priceGrowth'
  | 'basketGrowth';

export const GROWTH_SOURCE_FIELD: Record<RowGrowthType, keyof ClubGrowthRow> = {
  invoiceCountGrowth: 'invoiceCount',
  totalAmountGrowth: 'totalAmount',
  priceGrowth: 'avgItemPrice',
  basketGrowth: 'avgItemCount',
};

export const MARKET_PULSE_TO_ROW_GROWTH: Record<MarketPulseGrowthKey, RowGrowthType> = {
  salesGrowth: 'totalAmountGrowth',
  invoiceGrowth: 'invoiceCountGrowth',
  priceGrowth: 'priceGrowth',
  basketGrowth: 'basketGrowth',
};

/** فیلدهای حجمی که در حالت برآورد، بر اساس روزهای دریافت‌شده مقیاس می‌شوند */
export const VOLUME_SOURCE_FIELDS = new Set<keyof ClubGrowthRow>([
  'invoiceCount',
  'totalAmount',
]);

/**
 * فیلدهایی که در حالت برآورد مقیاس می‌شوند.
 * فقط حجم (فاکتور/فروش) — میانگین قیمت/سبد مقیاس نمی‌شوند.
 */
export const EXTRAPOLATABLE_SOURCE_FIELDS = VOLUME_SOURCE_FIELDS;

export function isVolumeSourceField(field: keyof ClubGrowthRow): boolean {
  return VOLUME_SOURCE_FIELDS.has(field);
}

export function isExtrapolatableSourceField(field: keyof ClubGrowthRow): boolean {
  return EXTRAPOLATABLE_SOURCE_FIELDS.has(field);
}

export function getExtrapolatedFieldValue(
  row: ClubGrowthRow,
  field: keyof ClubGrowthRow,
  sourceData: ClubGrowthRow[],
  extrapolate: boolean
): number {
  const raw = row[field];
  const value = typeof raw === 'number' && Number.isFinite(raw) ? raw : 0;
  // Only volume fields scale with fullDays/orderDays; averages stay raw.
  if (!extrapolate || !VOLUME_SOURCE_FIELDS.has(field)) return value;

  const { incomplete, orderDays, fullDays } = getIncompleteMonthInfo(row, sourceData);
  if (!incomplete || orderDays <= 0) return value;

  return value * (fullDays / orderDays);
}

const toEnglishDigits = (value: string): string =>
  value.replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)));

export function getJalaliNow(): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(new Date());

  const get = (type: string) =>
    parseInt(toEnglishDigits(parts.find((p) => p.type === type)?.value || '0'), 10);

  return { year: get('year'), month: get('month'), day: get('day') };
}

export function isJalaliLeapYear(jy: number): boolean {
  // Algorithm aligned with jalaali-js (jalaali calendar leap rule).
  const breaks = [-61, 9, 38, 199, 426, 686, 756, 818, 1111, 1181, 1210, 1635, 2060, 2097, 2192, 2262, 2324, 2394, 2456, 3178];
  let jump = 0;
  let jp = breaks[0];

  for (let i = 1; i < breaks.length; i++) {
    const jm = breaks[i];
    jump = jm - jp;
    if (jy < jm) break;
    jp = jm;
  }

  let n = jy - jp;
  if (jump - n < 6) {
    n = n - jump + Math.floor((jump + 4) / 33) * 33;
  }
  let leap = (((n + 1) % 33) - 1) % 4;
  if (leap < 0) leap += 4;
  return leap === 0;
}

export function getJalaliMonthDays(year: number, month: number): number {
  if (month <= 6) return 31;
  if (month <= 11) return 30;
  return isJalaliLeapYear(year) ? 30 : 29;
}

export function getYearMonthForRow(item: ClubGrowthRow): { year: number; month: number } {
  const month = parseInt(String(item.monthNum), 10);
  if (item.year) {
    return { year: item.year, month };
  }
  const parts = item.month.split('-');
  const year = parts.length > 1 ? parseInt(parts[0], 10) : new Date().getFullYear();
  return { year, month };
}

export function getRowMonthKey(item: ClubGrowthRow): string {
  const { year, month } = getYearMonthForRow(item);
  return `${year}-${String(month).padStart(2, '0')}`;
}

export function sortRowsChronologically<T extends ClubGrowthRow>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const aYm = getYearMonthForRow(a);
    const bYm = getYearMonthForRow(b);
    if (aYm.year !== bYm.year) return aYm.year - bYm.year;
    return aYm.month - bYm.month;
  });
}

export function sliceClubDisplayWindow<T>(rows: T[], baseMonths: number): T[] {
  if (!rows.length || baseMonths <= 0) return rows;
  return rows.slice(-baseMonths);
}

/**
 * «کل فاکتورهای بررسی‌شده» برای نبض بازار = مجموع فاکتورهای شعبه (در بازه)
 * + مجموع فاکتورهای همه شعب بازار (در همان بازه، از `sumInvoiceCount`).
 * قبلاً فقط `invoiceCount` شعبه جمع می‌شد و سهم بازار نادیده گرفته می‌شد؛
 * این تابع محاسبه را برای هر دو طرف یکسان و بدون میانگین‌گیری اشتباه انجام می‌دهد.
 */
/**
 * مجموع فاکتورهای بررسی‌شدهٔ «کل بازار» (نه شعبه) در بازهٔ نمایش —
 * شاخص اعتماد نبض بازار باید همیشه از دادهٔ کل بازار (SUM شعب واجدشرایط) استفاده کند،
 * نه فاکتورهای همان یک شعبه که عدد را کوچک و گمراه‌کننده می‌کند.
 */
export function computeMarketPulseInvoiceCountTotal(
  branchWindow: ClubGrowthRow[],
  marketWindow: ClubGrowthRow[],
): number {
  void branchWindow;
  return marketWindow.reduce((sum, row) => {
    const value = Number.isFinite(row.sumInvoiceCount)
      ? (row.sumInvoiceCount as number)
      : Number.isFinite(row.invoiceCount)
        ? row.invoiceCount
        : 0;
    return sum + value;
  }, 0);
}

export function isPartialCurrentMonthRow(
  row: ClubGrowthRow,
  sourceData: ClubGrowthRow[]
): boolean {
  const chronSorted = sortRowsChronologically(sourceData);
  const rowYm = getYearMonthForRow(row);
  const dataIndex = chronSorted.findIndex((r) => {
    const ym = getYearMonthForRow(r);
    return ym.year === rowYm.year && ym.month === rowYm.month;
  });

  if (dataIndex !== chronSorted.length - 1) return false;

  const jalaliNow = getJalaliNow();
  if (rowYm.year !== jalaliNow.year || rowYm.month !== jalaliNow.month) return false;

  const fullDays = getJalaliMonthDays(rowYm.year, rowYm.month);
  const daysPassed = Math.max(1, Math.min(jalaliNow.day - 1, fullDays));
  return daysPassed < fullDays;
}

export function getIncompleteMonthInfo(
  row: ClubGrowthRow,
  sourceData?: ClubGrowthRow[]
): IncompleteMonthInfo {
  const { year, month } = getYearMonthForRow(row);
  const fullDays = getJalaliMonthDays(year, month);

  if (typeof row.orderDays === 'number' && row.orderDays > 0) {
    const orderDays = Math.min(row.orderDays, fullDays);
    return { incomplete: orderDays < fullDays, orderDays, fullDays };
  }

  if (sourceData && isPartialCurrentMonthRow(row, sourceData)) {
    const jalaliNow = getJalaliNow();
    const orderDays = Math.max(1, Math.min(jalaliNow.day - 1, fullDays));
    return { incomplete: orderDays < fullDays, orderDays, fullDays };
  }

  return { incomplete: false, orderDays: fullDays, fullDays };
}

export function isIncompleteMonthRow(
  row: ClubGrowthRow,
  sourceData?: ClubGrowthRow[]
): boolean {
  return getIncompleteMonthInfo(row, sourceData).incomplete;
}

/**
 * رشد ماه‌به‌ماه: ((مقدار ماه جاری − ماه قبل) / ماه قبل) × 100
 * در حالت برآورد، ماه‌هایی که دادهٔ کامل ندارند برای کل ماه مقیاس می‌شوند.
 */
export function getRowGrowthValue(
  sourceData: ClubGrowthRow[],
  row: ClubGrowthRow,
  growthType: RowGrowthType,
  extrapolate = false
): number | null {
  if (!sourceData.length) return null;

  const chronSorted = sortRowsChronologically(sourceData);
  const rowYm = getYearMonthForRow(row);
  const dataIndex = chronSorted.findIndex((r) => {
    const ym = getYearMonthForRow(r);
    return ym.year === rowYm.year && ym.month === rowYm.month;
  });

  if (dataIndex <= 0) return null;

  const previousData = chronSorted[dataIndex - 1];
  if (!previousData) return null;

  const sourceField = GROWTH_SOURCE_FIELD[growthType];
  const shouldExtrapolate = extrapolate;

  let currentValue = getExtrapolatedFieldValue(row, sourceField, sourceData, shouldExtrapolate);
  let previousValue = getExtrapolatedFieldValue(previousData, sourceField, sourceData, shouldExtrapolate);

  if (previousValue === 0) return null;

  return ((currentValue - previousValue) / previousValue) * 100;
}

export function buildGrowthSeries(
  sourceData: ClubGrowthRow[],
  growthType: RowGrowthType,
  extrapolate = false
): Map<string, number | null> {
  const map = new Map<string, number | null>();
  sortRowsChronologically(sourceData).forEach((row) => {
    map.set(getRowMonthKey(row), getRowGrowthValue(sourceData, row, growthType, extrapolate));
  });
  return map;
}

export function findRowByYearMonth(
  sourceData: ClubGrowthRow[],
  year: number,
  month: number,
): ClubGrowthRow | undefined {
  return sortRowsChronologically(sourceData).find((row) => {
    const ym = getYearMonthForRow(row);
    return ym.year === year && ym.month === month;
  });
}

/**
 * رشد سال‌به‌سال: ((مقدار ماه جاری − ماه مشابه سال قبل) / ماه مشابه سال قبل) × 100
 * مثال: ۱۴۰۵/۰۴ نسبت به ۱۴۰۴/۰۴
 */
export function getRowYoYGrowthValue(
  sourceData: ClubGrowthRow[],
  row: ClubGrowthRow,
  growthType: RowGrowthType,
  extrapolate = false,
): number | null {
  if (!sourceData.length) return null;

  const rowYm = getYearMonthForRow(row);
  const previousYearRow = findRowByYearMonth(sourceData, rowYm.year - 1, rowYm.month);
  if (!previousYearRow) return null;

  const sourceField = GROWTH_SOURCE_FIELD[growthType];
  const currentValue = getExtrapolatedFieldValue(row, sourceField, sourceData, extrapolate);
  const previousValue = getExtrapolatedFieldValue(previousYearRow, sourceField, sourceData, extrapolate);

  if (previousValue === 0) return null;

  return ((currentValue - previousValue) / previousValue) * 100;
}

export function buildYoYGrowthSeries(
  sourceData: ClubGrowthRow[],
  growthType: RowGrowthType,
  extrapolate = false,
): Map<string, number | null> {
  const map = new Map<string, number | null>();
  sortRowsChronologically(sourceData).forEach((row) => {
    map.set(getRowMonthKey(row), getRowYoYGrowthValue(sourceData, row, growthType, extrapolate));
  });
  return map;
}

export function getMarketPulseGrowthValue(
  sourceData: ClubGrowthRow[],
  row: ClubGrowthRow,
  metricKey: MarketPulseGrowthKey,
  extrapolate = false
): number | null {
  return getRowGrowthValue(sourceData, row, MARKET_PULSE_TO_ROW_GROWTH[metricKey], extrapolate);
}
